// SPDX-License-Identifier: MIT
// Source AUTORITATIVE de la jauge : la sortie de `claude -p "/usage"`. Claude
// donne directement le % de la fenêtre courante + l'heure de reset — bien plus
// fiable que la reconstruction par comptage de tokens JSONL (qui exigeait un
// plafond deviné). Tout est pur/testable ici ; l'I/O (spawn claude) vit côté Rust.
import type { WindowState } from './usage-types';
import { APPROACHING_PCT } from './usage-types';

export interface UsageReport {
  sessionPct: number;               // % de la fenêtre courante (5 h) — autoritatif
  sessionResetsAt: number | null;   // epoch s
  weekAllPct: number | null;        // % hebdo tous modèles
  weekAllResetsAt: number | null;
  weekSonnetPct: number | null;
}

const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

// "Jun 18, 9:39am (Europe/Paris)" → epoch s. La chaîne est DÉJÀ dans le fuseau
// local de l'user (Claude la localise) → on construit une Date locale.
export function parseResetToEpoch(s: string, nowMs: number): number | null {
  const cleaned = s.replace(/\([^)]*\)/g, '').trim(); // retire " (Europe/Paris)"
  const m = cleaned.match(/^([A-Za-z]{3,})\s+(\d{1,2}),?\s+(\d{1,2}):(\d{2})\s*([ap]m)$/i);
  if (!m) return null;
  const mon = MONTHS[m[1].slice(0, 3).toLowerCase()];
  if (mon == null) return null;
  const day = parseInt(m[2], 10);
  let hr = parseInt(m[3], 10) % 12;
  if (/pm/i.test(m[5])) hr += 12;
  const min = parseInt(m[4], 10);
  const year = new Date(nowMs).getFullYear();
  const d = new Date(year, mon, day, hr, min, 0, 0);
  let t = d.getTime();
  if (!Number.isFinite(t)) return null;
  // bord d'année : un reset > 8 j dans le passé = en réalité l'an prochain
  if (t < nowMs - 8 * 86_400_000) { d.setFullYear(year + 1); t = d.getTime(); }
  return Math.floor(t / 1000);
}

function pctLine(text: string, label: RegExp): { pct: number; reset: string | null } | null {
  const m = text.match(label);
  if (!m) return null;
  return { pct: parseInt(m[1], 10), reset: m[2] ? m[2].trim() : null };
}

// Parse la sortie texte de `/usage`. null si la ligne « Current session » manque
// (claude pas connecté, format inattendu → dégradation gracieuse §4).
export function parseUsageReport(text: string, nowMs: number): UsageReport | null {
  const session = pctLine(text, /Current session:\s*(\d+)%\s*used(?:\s*·\s*resets\s*(.+))?/i);
  if (!session) return null;
  const weekAll = pctLine(text, /Current week \(all models\):\s*(\d+)%\s*used(?:\s*·\s*resets\s*(.+))?/i);
  const weekSonnet = pctLine(text, /Current week \(Sonnet only\):\s*(\d+)%\s*used/i);
  return {
    sessionPct: session.pct,
    sessionResetsAt: session.reset ? parseResetToEpoch(session.reset, nowMs) : null,
    weekAllPct: weekAll ? weekAll.pct : null,
    weekAllResetsAt: weekAll?.reset ? parseResetToEpoch(weekAll.reset, nowMs) : null,
    weekSonnetPct: weekSonnet ? weekSonnet.pct : null,
  };
}

// Étiquette de forfait affichable depuis le `subscriptionType` de `claude auth
// status` ("max" → "Max", "pro" → "Pro"). null si inconnu. (subscriptionType ne
// distingue pas Max 5×/20× — on affiche "Max".)
export function planLabelFromSubscription(subscriptionType: string | null | undefined): string | null {
  if (!subscriptionType) return null;
  const s = subscriptionType.trim().toLowerCase();
  if (!s) return null;
  if (s === 'pro') return 'Pro';
  if (s === 'max') return 'Max';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Construit un WindowState autoritatif depuis le rapport /usage. fillPct = % de la
// fenêtre courante donné par Claude (pas de plafond à deviner). Le throttle live
// reste détecté en parallèle par le tap rate_limit (fenêtre vs limite hebdo).
export function windowStateFromReport(r: UsageReport, _nowMs: number, planLabel: string | null = null): WindowState {
  const fillPct = Math.max(0, Math.min(100, r.sessionPct));
  const status = fillPct >= 100 ? 'throttled' : fillPct >= APPROACHING_PCT ? 'approaching' : 'ok';
  return {
    planTier: 'unknown',
    status,
    resetsAt: r.sessionResetsAt,
    fillPct,
    burnRatePerMin: null,
    projectionMins: null,
    confidence: 'authoritative',
    source: 'usage-command',
    windowTokens: 0,
    planLabel,
  };
}
